// Registration Service
// Handles user registration API calls

import {
  RegistrationRequest,
  RegistrationResponse,
  EmailVerificationRequest,
  EmailVerificationResponse,
  AvailabilityCheckResponse,
  PasswordResetRequest,
  PasswordResetResponse,
  PasswordResetConfirmRequest,
  PasswordResetConfirmResponse
} from '../types/registration';

/**
 * Registration Service Configuration
 */
export interface RegistrationConfig {
  apiBaseUrl: string;
  registerEndpoint: string;
  verifyEmailEndpoint: string;
  resendVerificationEndpoint: string;
  checkUsernameEndpoint: string;
  checkEmailEndpoint: string;
  passwordResetEndpoint: string;
  passwordResetConfirmEndpoint: string;
  timeout: number;
}

/**
 * Default registration API configuration
 */
const defaultRegistrationConfig: RegistrationConfig = {
  apiBaseUrl: process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'production' 
    ? 'https://api.example.com' // Replace with actual production URL
    : 'http://localhost:5000'),
  registerEndpoint: '/api/auth/register',
  verifyEmailEndpoint: '/api/auth/verify-email',
  resendVerificationEndpoint: '/api/auth/resend-verification',
  checkUsernameEndpoint: '/api/auth/check-username',
  checkEmailEndpoint: '/api/auth/check-email',
  passwordResetEndpoint: '/api/auth/forgot-password',
  passwordResetConfirmEndpoint: '/api/auth/reset-password',
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '10000', 10)
};

/**
 * Registration Service Class
 */
export class RegistrationService {
  private config: RegistrationConfig;

  constructor(config?: Partial<RegistrationConfig>) {
    this.config = { ...defaultRegistrationConfig, ...config };
    
    // Only log in development mode
    if (process.env.NODE_ENV === 'development') {
      this.log('[RegistrationService] Initialized with config:', {
        apiBaseUrl: this.config.apiBaseUrl,
        registerEndpoint: this.config.registerEndpoint,
        timeout: this.config.timeout
      });
    }
  }

  /**
   * Conditional logging helper (only logs in development)
   */
  private log(message: string, ...args: any[]): void {
    if (process.env.NODE_ENV === 'development') {
      console.log(message, ...args);
    }
  }

  /**
   * Register a new user
   */
  async registerUser(request: RegistrationRequest): Promise<RegistrationResponse> {
    this.log('[RegistrationService] Starting user registration...');
    this.log('[RegistrationService] Username:', request.username);

    try {
      const url = `${this.config.apiBaseUrl}${this.config.registerEndpoint}`;
      this.log('[RegistrationService] Request URL:', url);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(request),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      this.log('[RegistrationService] API response received');
      this.log('[RegistrationService] Response status:', response.status);
      this.log('[RegistrationService] Response ok:', response.ok);

      if (!response.ok) {
        let errorMessage = 'Registration failed';

        if (response.status === 400) {
          errorMessage = 'Invalid registration data';
        } else if (response.status === 409) {
          errorMessage = 'Username or email already exists';
        } else if (response.status === 500) {
          errorMessage = 'Server error - please try again later';
        }

        // Try to get detailed error from response
        try {
          const errorData = await response.json();
          this.log('[RegistrationService] Error response data:', errorData);

          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          this.log('[RegistrationService] Could not parse error response');
        }

        return {
          success: false,
          message: errorMessage,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();
      this.log('[RegistrationService] Registration successful');
      this.log('[RegistrationService] Response data received');

      return {
        success: data.success !== false,
        user: data.user,
        message: data.message || 'Registration successful',
        requiresEmailVerification: data.requiresEmailVerification,
        verificationEmailSent: data.verificationEmailSent
      };

    } catch (error: any) {
      this.log('[RegistrationService] Registration error:', error);

      let errorMessage;

      if (error.name === 'AbortError') {
        errorMessage = 'Request timeout - please try again';
      } else if (error.message?.includes('fetch')) {
        errorMessage = 'Cannot connect to server';
      } else {
        errorMessage = `Connection error: ${error.message}`;
      }

      return {
        success: false,
        message: errorMessage,
        error: error.message || 'Unknown error occurred'
      };
    }
  }

  /**
   * Verify email with token
   */
  async verifyEmail(request: EmailVerificationRequest): Promise<EmailVerificationResponse> {
    this.log('[RegistrationService] Verifying email...');

    try {
      const url = `${this.config.apiBaseUrl}${this.config.verifyEmailEndpoint}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(request)
      });

      this.log('[RegistrationService] Email verification response:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          message: errorData.message || 'Email verification failed',
          error: `HTTP ${response.status}`
        };
      }

      const data = await response.json();
      return {
        success: true,
        message: data.message || 'Email verified successfully'
      };

    } catch (error: any) {
      this.log('[RegistrationService] Email verification error:', error);
      return {
        success: false,
        message: 'Email verification failed',
        error: error.message
      };
    }
  }

  /**
   * Resend verification email
   */
  async resendVerificationEmail(email: string): Promise<EmailVerificationResponse> {
    this.log('[RegistrationService] Resending verification email...');

    try {
      const url = `${this.config.apiBaseUrl}${this.config.resendVerificationEndpoint}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      this.log('[RegistrationService] Resend verification response:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          message: errorData.message || 'Failed to resend verification email',
          error: `HTTP ${response.status}`
        };
      }

      const data = await response.json();
      return {
        success: true,
        message: data.message || 'Verification email sent'
      };

    } catch (error: any) {
      this.log('[RegistrationService] Resend verification error:', error);
      return {
        success: false,
        message: 'Failed to resend verification email',
        error: error.message
      };
    }
  }

  /**
   * Check if username is available
   */
  async checkUsernameAvailability(username: string): Promise<AvailabilityCheckResponse> {
    this.log('[RegistrationService] Checking username availability:', username);

    try {
      const url = `${this.config.apiBaseUrl}${this.config.checkUsernameEndpoint}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ username })
      });

      if (!response.ok) {
        this.log('[RegistrationService] Username check failed:', response.status);
        return { available: true }; // Fail gracefully, allow form submission
      }

      const data = await response.json();
      this.log('[RegistrationService] Username availability:', data.available);

      return {
        available: data.available !== false,
        message: data.message
      };

    } catch (error: any) {
      this.log('[RegistrationService] Username check error:', error);
      return { available: true }; // Fail gracefully
    }
  }

  /**
   * Check if email is available
   */
  async checkEmailAvailability(email: string): Promise<AvailabilityCheckResponse> {
    this.log('[RegistrationService] Checking email availability:', email);

    try {
      const url = `${this.config.apiBaseUrl}${this.config.checkEmailEndpoint}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        this.log('[RegistrationService] Email check failed:', response.status);
        return { available: true }; // Fail gracefully
      }

      const data = await response.json();
      this.log('[RegistrationService] Email availability:', data.available);

      return {
        available: data.available !== false,
        message: data.message
      };

    } catch (error: any) {
      this.log('[RegistrationService] Email check error:', error);
      return { available: true }; // Fail gracefully
    }
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(request: PasswordResetRequest): Promise<PasswordResetResponse> {
    this.log('[RegistrationService] Requesting password reset...');

    try {
      const url = `${this.config.apiBaseUrl}${this.config.passwordResetEndpoint}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(request)
      });

      this.log('[RegistrationService] Password reset response:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          message: errorData.message || 'Failed to send password reset email',
          error: `HTTP ${response.status}`
        };
      }

      const data = await response.json();
      return {
        success: true,
        message: data.message || 'Password reset email sent',
        resetEmailSent: true
      };

    } catch (error: any) {
      this.log('[RegistrationService] Password reset error:', error);
      return {
        success: false,
        message: 'Failed to send password reset email',
        error: error.message
      };
    }
  }

  /**
   * Confirm password reset with new password
   */
  async confirmPasswordReset(request: PasswordResetConfirmRequest): Promise<PasswordResetConfirmResponse> {
    this.log('[RegistrationService] Confirming password reset...');

    try {
      const url = `${this.config.apiBaseUrl}${this.config.passwordResetConfirmEndpoint}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(request)
      });

      this.log('[RegistrationService] Password reset confirmation response:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          message: errorData.message || 'Failed to reset password',
          error: `HTTP ${response.status}`
        };
      }

      const data = await response.json();
      return {
        success: true,
        message: data.message || 'Password reset successfully'
      };

    } catch (error: any) {
      this.log('[RegistrationService] Password reset confirmation error:', error);
      return {
        success: false,
        message: 'Failed to reset password',
        error: error.message
      };
    }
  }
}

// Export singleton instance
export const registrationService = new RegistrationService();
